import { useId, useEffect, useRef, useReducer } from "react";
import { Upload } from "tus-js-client";
import { v4 as uuid } from "uuid";
import lodash from "lodash";
import Cookies from "js-cookie";
import classNames from "classnames";
import { _TEST1234, api } from "@/comn/features/config/ApiConfig";
import { IconButton } from "../IconButton";
import { Pdf } from "../Pdf";
import { useModal, useToast } from "@/comn/hooks";
import { useTranslation } from "react-i18next";
import { Button } from "../Button";
import { comnUtils } from "@/comn/utils";

const initialState = {
    files: [],
    checked: [],
    status: null,
    thumbnailUrl: null,
};

const initializer = (value: any) => {
    let files: any = [];
    if (value && typeof value === "object") {
        if (Array.isArray(value?.fileSaveReqDtoList)) {
            files = value.fileSaveReqDtoList;
        }
    }
    return () => ({ ...initialState, files });
};

const reducer = (state: any, { type, payload }: any) => {
    switch (type) {
        case "setFileName": {
            const { value, data } = payload;
            const files = state.files.map((item: any) => {
                if (item.fileUuid !== data.fileUuid) return item;
                return { ...item, fileNm: value };
            });
            return { ...state, files };
        }

        case "setFileTitle": {
            const { value, data } = payload;
            const files = state.files.map((item: any) => {
                if (item.fileUuid !== data.fileUuid) return item;
                return { ...item, fileClsfNm: value };
            });
            return { ...state, files };
        }

        case "setFileDescription": {
            const { value, data } = payload;
            const files = state.files.map((item: any) => {
                if (item.fileUuid !== data.fileUuid) return item;
                return { ...item, fileDesc: value };
            });
            return { ...state, files };
        }

        case "loadingStart":
            return { ...state, status: "loading" };
        case "loadingEnd":
            return { ...state, status: null };
        case "setOrigin": {
            return { ...state, files: lodash.cloneDeep(payload.files), thumbnailUrl: payload.thumbnailUrl };
        }
        case "addFiles":
            return {
                ...state,
                files: [...state.files, ...lodash.cloneDeep(payload.files)],
                thumbnailUrl: payload.thumbnailUrl,
            };
        case "replaceFiles":
            return {
                ...state,
                files: state.files.map((file: any) => {
                    const target = payload.files.find(({ key }: any) => key === file.key);
                    return target ? lodash.cloneDeep(target) : file;
                }),
                thumbnailUrl: payload.thumbnailUrl,
            };
        case "replaceFile":
            return {
                ...state,
                files: state.files.map((file: any) => {
                    return file.key === payload.key ? lodash.cloneDeep(payload) : file;
                }),
            };
        case "removeFileByKey":
            return { ...state, files: state.files.filter(({ key }: any) => key !== payload) };
        case "removeFileByUuid":
            return { ...state, files: state.files.filter(({ fileUuid }: any) => fileUuid !== payload) };
        case "check":
            const some = state.checked.some(({ fileUuid, key }: any) => {
                if (payload.key) return key === payload.key;
                if (payload.fileUuid) return fileUuid === payload.fileUuid;
            });
            return {
                ...state,
                checked: some
                    ? state.checked.filter(({ fileUuid, key }: any) => {
                          if (payload.key) return key !== payload.key;
                          if (payload.fileUuid) return fileUuid !== payload.fileUuid;
                      })
                    : [...state.checked, payload],
            };
        case "reset":
            return { ...initialState };
    }
};

const TableItem = (props: any) => {
    const {
        data,
        checked,
        rootEdit,
        retry,
        download,
        handleCheck,
        handleChangeName,
        handleChangeTitle,
        handleChangeDescription,
        valueType,
        // options
        readOnlyName,
        readOnlyTitle,
        readOnlyDescription,
        disableName,
        disableTitle,
        disableDescription,
        disableDelete,
    } = props;

    return (
        <tr>
            {rootEdit && (
                <td className="w-10">
                    <div className="flex items-center justify-center">
                        {(!disableDelete || data.__type !== "origin") && (
                            <input
                                type="checkbox"
                                checked={checked.some(({ fileUuid, key }: any) => {
                                    if (data.key) return key === data.key;
                                    if (data.fileUuid) return fileUuid === data.fileUuid;
                                })}
                                onChange={(event) => handleCheck(event, data)}
                            />
                        )}
                    </div>
                </td>
            )}
            {valueType !== "file" && !disableName && (
                <td>
                    {rootEdit ? (
                        <input
                            type="text"
                            className="input"
                            value={data.fileNm || ""}
                            disabled={
                                data.status ||
                                readOnlyName === true ||
                                (readOnlyName === "origin" && data.__type === "origin")
                            }
                            onChange={(event) => handleChangeName(event, data)}
                        />
                    ) : (
                        data.fileNm
                    )}
                </td>
            )}
            {valueType !== "file" && !disableTitle && (
                <td>
                    {rootEdit ? (
                        <input
                            type="text"
                            className="input"
                            value={data.fileClsfNm || ""}
                            disabled={
                                data.status ||
                                readOnlyTitle === true ||
                                (readOnlyTitle === "origin" && data.__type === "origin")
                            }
                            onChange={(event) => handleChangeTitle(event, data)}
                        />
                    ) : (
                        data.fileClsfNm
                    )}
                </td>
            )}
            {valueType !== "file" && !disableDescription && (
                <td>
                    {rootEdit ? (
                        <input
                            type="text"
                            className="input"
                            value={data.fileDesc || ""}
                            disabled={
                                data.status ||
                                readOnlyDescription === true ||
                                (readOnlyDescription === "origin" && data.__type === "origin")
                            }
                            onChange={(event) => handleChangeDescription(event, data)}
                        />
                    ) : (
                        data.fileDesc
                    )}
                </td>
            )}
            <td>
                <span className="cursor-pointer underline text-blue" onClick={() => download(data)}>
                    {data.fileNm || data.name}
                </span>
                {data.status === "pending" && (
                    <IconButton icon="loading" className="animate-spin text-blue" size="xs" />
                )}
                {(data.status === "error" || data.status === "success") && (
                    <IconButton
                        icon="exclamationTriangle"
                        className="text-error"
                        size="xs"
                        onClick={() => retry(data)}
                    />
                )}
            </td>
        </tr>
    );
};

/**
 * size, min, max
 * @param props
 * @returns
 */
export const InputFile = (props: any) => {
    let {
        edit = true,
        mode,
        value,
        onChange,
        multiple,
        bsopClsfCd,
        accept,

        pdfViewer,

        thumbnail,
        thumbnailPlaceholder,
        thumbnailAlign = "left",
        thumbnailHeight,
        thumbnailWidth,
        valueType,

        readOnlyName,
        readOnlyTitle,
        readOnlyDescription,

        disableHeader,
        disableName,
        disableTitle,
        disableDescription,

        disableDelete,
        disableRemoveButton,

        isPublic,

        // 250618
        allow,
    } = props;

    let sys = "",
        base = "";
    switch (process.env.REACT_APP_SYSTEM_GROUP) {
        case "tancis":
            base = "api/v1/ptli/comn/files";
            sys = process.env.REACT_APP_API_PTLI || "";
            break;
        case "tanoga":
        case "tanesw":
        case "tancis":
            // base = "api/v1/ptli/comn/files" || "";
            // sys = process.env.REACT_APP_API_PTLI || "";
            base = "api/v1/ptle/comn/files";
            sys = process.env.REACT_APP_API_PTLE || "";
            break;
    }
    const fileGetUrl = isPublic ? `${sys}/${base}/public` : `${sys}/${base}`;
    const tempUploadUrl = `${sys}/${base}/upload`;
    const fileUploadUrl = isPublic ? `${sys}/${base}/public` : `${sys}/${base}`;
    const fileDownloadUrl = isPublic ? `${sys}/${base}/public/download` : `${sys}/${base}/download`;

    const _uniqueId = useRef(uuid());
    const _edit = mode === "edit" ? true : mode === "view" ? false : edit;
    const _context = useRef<any>({
        id: null,
        status: null,
        files: (() => {
            if (value && typeof value === "object") {
                if (Array.isArray(value?.fileSaveReqDtoList)) {
                    return value.fileSaveReqDtoList;
                }
            }
            return [];
        })(),
        origin: [],
    });
    const { openModal } = useModal();
    const { showToast } = useToast();

    const [state, dispatch] = useReducer(reducer, initialState, initializer(value));
    const { files, checked, status, thumbnailUrl } = state;

    useEffect(() => {
        if (valueType === "file") return;
        if (value === undefined) {
            _context.current = { id: null, status: null, files: [], origin: [] };
            dispatch({ type: "reset" });
            return;
        }
        if (typeof value !== "string") return;
        setOrigin(value);
    }, [value]);

    const loadStart = () => {
        if (_context.current.status === "loading") return true;
        _context.current.status = "loading";
        dispatch({ type: "loadingStart" });
    };

    const loadEnd = () => {
        _context.current.status = null;
        dispatch({ type: "loadingEnd" });
    };

    const handleChangeValue = (data?: any) => {
        if (!onChange) return;

        if (valueType === "file") {
            if (multiple) {
                onChange([...(value || []), ...data]);
            } else {
                onChange(data);
            }
            return;
        }

        onChange({
            fileGrpUuid: _context.current.id,
            fileSaveReqDtoList: _context.current.files
                .filter(({ fileStatCd, __type }: any) => {
                    return fileStatCd === "I" || fileStatCd === "D" || fileStatCd === "U" || __type === "origin";
                })
                .map(({ fileGrpUuid, fileUuid, fileStatCd, fileNm, fileClsfNm, fileDesc, __type }: any) => {
                    const item: any = {};
                    item.fileGrpUuid = fileGrpUuid;
                    item.fileUuid = fileUuid;
                    item.fileNm = fileNm;
                    item.fileClsfNm = fileClsfNm;
                    item.fileDesc = fileDesc;
                    item.fileStatCd = fileStatCd;
                    item.__type = __type;
                    item.publFileYn = isPublic ? "Y" : "N";
                    return item;
                }),
        });
    };

    /**
     * set origin from group id
     * @param id
     * @returns
     */
    const setOrigin = async (id: any) => {
        if (loadStart()) return;

        const content = await getFiles(id);

        if (content && content.length) {
            _context.current.id = content[0].fileGrpUuid;
            _context.current.origin = content;
            _context.current.files = lodash.cloneDeep(content);

            let thumbnailUrl;
            if (thumbnail) {
                const blob = await getBlob(content[0]);
                thumbnailUrl = URL.createObjectURL(blob);
            }

            dispatch({ type: "setOrigin", payload: { files: content, thumbnailUrl } });
            handleChangeValue();
        }

        loadEnd();
    };

    /**
     * get files by group id
     * @param id
     * @returns
     */
    const getFiles = async (id: any) => {
        try {
            const response = await api.get(fileGetUrl + `/${window.encodeURIComponent(id)}`);
            return response.data.fileDtlInfoList.content;
        } catch (error) {
            console.log(error);
        }
    };

    const getBlob = async (data: any) => {
        try {
            const response = await api.post(
                fileDownloadUrl,
                {
                    fileGrpUuid: data.fileGrpUuid,
                    fileUuid: data.fileUuid,
                },
                { responseType: "blob" },
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const downloadFile = async (data: any) => {
        try {
            const blob = await getBlob(data);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const nm: string = data.fileNm || "";
            const ext: string = data.fileExt ? "." + data.fileExt : "";

            a.download = nm.endsWith(ext) ? nm : nm + ext;
            a.href = url;
            a.click();
            a.remove();
        } catch (error) {
            console.log(error);
        }
    };

    const handleChange = async (event: any) => {
        // 250618
        if (typeof allow === "string" || Array.isArray(allow)) {
            const allowList = typeof allow === "string" ? [allow] : allow;

            const rejected = [...event.target.files].some((file) => {
                const filename = file.name;
                const extension = filename.split(".").pop().toLowerCase();
                return !allowList.includes(extension);
            });

            if (rejected) {
                event.target.value = "";
                showToast({ type: "error", content: "msg.com.00156" });
                return;
            }
        }

        if (valueType === "file") {
            if (multiple) {
                handleChangeValue(event.target.files);
            } else {
                handleChangeValue(event.target.files[0]);
            }
            return;
        }

        if (!multiple) {
            if (files.length) {
                event.target.value = "";
                return;
            }
        }

        if (loadStart()) return;

        const _files = Array.from(event.target.files)
            .filter((file: any) => {
                if (!file.size) showToast({ content: "msg.com.00141", uniqueId: _uniqueId.current });
                return file.size > 0;
            })
            .map((file: any) => {
                return {
                    file,
                    fileNm: file.name,
                    fileSize: file.size,
                    url: null,
                    key: uuid(),
                    status: "pending",
                    __type: "added",
                };
            });
        event.target.value = "";

        if (!_files.length) {
            loadEnd();
            return;
        }

        let thumbnailUrl;
        if (thumbnail) {
            thumbnailUrl = URL.createObjectURL(_files[0].file);
        }

        _context.current.files = [..._context.current.files, ..._files];
        dispatch({ type: "addFiles", payload: { files: _files, thumbnailUrl } });

        const _: any = [];
        if (_context.current.id) {
            const promises = _files.map((file) => {
                return new Promise(async (resolve) => {
                    const t: any = await uploadTemp(file);
                    if (!t.url) return resolve(t);
                    const f = await uploadFile(t);
                    resolve(f);
                });
            });
            const settled = await Promise.allSettled(promises);
            const result = settled.map((value: any) => value.value);
            _.push(...result);
        } else {
            for await (const file of _files) {
                const t: any = await uploadTemp(file);
                if (!t.url) {
                    _.push(t);
                    continue;
                } else {
                    const f = await uploadFile(t);
                    _.push(f);
                    break;
                }
            }

            if (_files.length > _.length) {
                const rest = _files.slice(_.length);
                const promises = rest.map((file) => {
                    return new Promise(async (resolve) => {
                        const t: any = await uploadTemp(file);
                        if (!t.url) return resolve(t);
                        const f = await uploadFile(t);
                        resolve(f);
                    });
                });
                const settled = await Promise.allSettled(promises);
                const result = settled.map((value: any) => value.value);
                _.push(...result);
            }
        }

        _context.current.files.forEach((file: any, index: any) => {
            const target = _.find(({ key }: any) => key === file.key);
            if (target) _context.current.files[index] = target;
        });
        dispatch({ type: "replaceFiles", payload: { files: _, thumbnailUrl } });

        handleChangeValue();
        loadEnd();
    };

    const uploadTemp = (data: any) => {
        const { file } = data;
        return new Promise((resolve, reject) => {
            const authorization = _TEST1234.AUTHORIZATION;
            const upload = new Upload(file, {
                endpoint: tempUploadUrl,
                overridePatchMethod: true,
                retryDelays: [0, 3000, 5000],
                headers: {
                    Authorization: comnUtils.isEmpty(_TEST1234.AUTHORIZATION)
                        ? ""
                        : `Bearer ${_TEST1234.AUTHORIZATION}`,
                },
                removeFingerprintOnSuccess: true,
                metadata: {
                    filename: file.name,
                    filetype: file.type,
                },
                onProgress: (bytesSent, bytesTotal) => {
                    const percentage = (bytesSent / bytesTotal) * 100;
                },
                onSuccess: () => {
                    resolve({ ...data, status: "success", url: upload.url });
                    return;
                    // dev
                    if (Math.random() > 0.7) resolve({ ...data, status: "success", url: upload.url });
                    else resolve({ ...data, status: "error", url: null });
                },
                onError: () => {
                    resolve({ ...data, status: "error", url: null });
                },
            });
            upload.start();
        });
    };

    const uploadFile = async (data: any) => {
        const id = _context.current.id;

        const payload: any = {};
        payload.publFileYn = isPublic ? "Y" : "N";
        if (id) payload.fileGrpUuid = id;
        switch (data.__type) {
            case "added":
                payload.fileNm = data.fileNm;
                payload.fileSize = data.fileSize;
                payload.fileUrl = data.url;
                payload.bsopClsfCd = bsopClsfCd;
                payload.__type = data.__type;

                break;
            case "deleted":
                payload.fileNm = data.fileNm;
                payload.fileSize = data.fileSize;
                payload.fileUuid = data.fileUuid;
                payload.bsopClsfCd = bsopClsfCd;
                payload.__type = data.__type;
                break;
        }

        try {
            const response = await api.post(fileUploadUrl, [payload]);
            const content = response.data.fileDtlInfoList.content[0];

            // set group id, if null
            if (!_context.current.id) {
                _context.current.id = content.fileGrpUuid;
            }

            return {
                key: data.key,
                fileNm: data.fileNm,
                fileSize: data.fileSize,
                fileGrpUuid: content.fileGrpUuid,
                fileUuid: content.fileUuid,
                fileStatCd: content.fileStatCd,
            };
        } catch (error) {
            console.log(error);
            return data;
        }
    };

    const remove = async (data: any, load = true) => {
        if (valueType === "file") {
            if (onChange) {
                onChange(undefined);
            }
            return;
        }

        if (data.key) {
            _context.current.files = _context.current.files.filter(({ key }: any) => {
                return key !== data.key;
            });
            dispatch({ type: "removeFileByKey", payload: data.key });
            handleChangeValue();
            return;
        }

        if (load) if (loadStart()) return;

        const f: any = await uploadFile({ ...data, __type: "deleted" });
        _context.current.files.forEach((file: any, index: any) => {
            if (file.fileUuid === f.fileUuid) _context.current.files[index] = f;
        });
        dispatch({ type: "removeFileByUuid", payload: f.fileUuid });

        handleChangeValue();
        if (load) loadEnd();
    };

    const retry = async (data: any) => {
        if (loadStart()) return;

        let t: any = data;
        if (data.status === "error") {
            t = await uploadTemp(data);
            if (!t.url) {
                loadEnd();
                return;
            }
        }

        const f: any = await uploadFile(t);

        _context.current.files.forEach((file: any, index: any) => {
            if (file.key === f.key) _context.current.files[index] = f;
        });
        dispatch({ type: "replaceFile", payload: f });

        handleChangeValue();
        loadEnd();
    };

    const handleCheck = (event: any, data: any) => {
        dispatch({ type: "check", payload: data });
    };

    const handleChangeName = (event: any, data: any) => {
        if (!Array.isArray(value?.fileSaveReqDtoList)) return;

        _context.current.files.forEach((file: any, index: any) => {
            if (file.fileUuid === data.fileUuid)
                _context.current.files[index] = {
                    ..._context.current.files[index],
                    fileNm: event.target.value,
                    fileStatCd: "U",
                };
        });

        dispatch({ type: "setFileName", payload: { value: event.target.value, data } });
        handleChangeValue();
    };

    const handleChangeTitle = (event: any, data: any) => {
        if (!Array.isArray(value?.fileSaveReqDtoList)) return;

        _context.current.files.forEach((file: any, index: any) => {
            if (file.fileUuid === data.fileUuid)
                _context.current.files[index] = {
                    ..._context.current.files[index],
                    fileClsfNm: event.target.value,
                    fileStatCd: "U",
                };
        });

        dispatch({ type: "setFileTitle", payload: { value: event.target.value, data } });
        handleChangeValue();
    };

    const handleChangeDescription = (event: any, data: any) => {
        if (!Array.isArray(value?.fileSaveReqDtoList)) return;

        _context.current.files.forEach((file: any, index: any) => {
            if (file.fileUuid === data.fileUuid)
                _context.current.files[index] = {
                    ..._context.current.files[index],
                    fileDesc: event.target.value,
                    fileStatCd: "U",
                };
        });

        dispatch({ type: "setFileDescription", payload: { value: event.target.value, data } });
        handleChangeValue();
    };

    const removeChecked = async () => {
        if (loadStart()) return;

        await Promise.allSettled(
            checked.map((data: any) => {
                return remove(data, false);
            }),
        );

        handleChangeValue();
        loadEnd();
    };

    const openPdf = async (data: any) => {
        if (!data) return;

        try {
            const response = await api.post(
                fileDownloadUrl,
                {
                    fileGrpUuid: data.fileGrpUuid,
                    fileUuid: data.fileUuid,
                },
                { responseType: "arraybuffer" },
            );

            openModal({ type: "pdf", data: { buffer: response.data, __t: new Date(), filename: "test" } });
        } catch (error) {
            throw error;
        }
    };

    return (
        <>
            {multiple && (
                <Multi
                    pdfViewer={pdfViewer}
                    _edit={_edit}
                    data={valueType === "file" ? Array.from(value || []) : files}
                    accept={accept}
                    handleChange={handleChange}
                    removeChecked={removeChecked}
                    disableHeader={disableHeader}
                    disableDelete={disableDelete}
                    disableName={disableName}
                    disableTitle={disableTitle}
                    disableDescription={disableDescription}
                    disableRemoveButton={disableRemoveButton}
                    checked={checked}
                    retry={retry}
                    downloadFile={downloadFile}
                    handleCheck={handleCheck}
                    handleChangeName={handleChangeName}
                    handleChangeTitle={handleChangeTitle}
                    handleChangeDescription={handleChangeDescription}
                    readOnlyName={readOnlyName}
                    readOnlyTitle={readOnlyTitle}
                    readOnlyDescription={readOnlyDescription}
                    valueType={valueType}
                />
            )}
            {!multiple && (
                <Single
                    pdfViewer={pdfViewer}
                    _edit={_edit}
                    data={valueType === "file" ? value : files[0]}
                    thumbnail={thumbnail}
                    thumbnailPlaceholder={thumbnailPlaceholder}
                    thumbnailUrl={thumbnailUrl}
                    thumbnailAlign={thumbnailAlign}
                    thumbnailHeight={thumbnailHeight}
                    thumbnailWidth={thumbnailWidth}
                    valueType={valueType}
                    accept={accept}
                    handleChange={handleChange}
                    retry={retry}
                    remove={remove}
                    downloadFile={downloadFile}
                    openPdf={openPdf}
                    disableRemoveButton={disableRemoveButton}
                />
            )}
        </>
    );
};

const Single = (props: any) => {
    const { t } = useTranslation();

    const {
        openPdf,
        pdfViewer,
        _edit,
        data,
        thumbnail,
        thumbnailPlaceholder,
        thumbnailUrl,
        thumbnailAlign,
        thumbnailHeight,
        thumbnailWidth,
        accept,
        handleChange,
        retry,
        remove,
        downloadFile,

        disableRemoveButton,
    } = props;

    const { fileNm, name } = data || {};

    return (
        <div className="w-full flex flex-col gap-1">
            {!!thumbnail &&
                (data || thumbnailPlaceholder ? (
                    <img
                        src={data ? thumbnailUrl : null}
                        alt="thumbnail"
                        className={classNames(
                            "w-full object-contain",
                            thumbnailAlign === "left" && "object-left",
                            thumbnailAlign === "center" && "object-center",
                            thumbnailAlign === "right" && "object-right",
                        )}
                        style={{ height: thumbnailHeight, width: thumbnailWidth, aspectRatio: 1 }}
                    />
                ) : null)}

            {!_edit && (
                <div className={classNames("px-1.5 flex items-center gap-1")}>
                    <span
                        className={classNames(!!fileNm && "cursor-pointer underline text-blue")}
                        onClick={() => {
                            if (!fileNm) return;
                            downloadFile(data);
                        }}
                    >
                        {fileNm || name || "No file."}
                    </span>
                    {pdfViewer && <IconButton icon="pdf" size="sm" onClick={() => openPdf(data)} />}
                </div>
            )}

            <div hidden={!_edit}>
                <div className="input flex items-center justify-between p-0 h-8">
                    <label className="flex border-r h-full items-center justify-center px-3 cursor-pointer bg-primary rounded-l text-white max-w-[40%] overflow-hidden whitespace-nowrap text-ellipsis">
                        <input hidden accept={accept} type="file" onChange={handleChange} />
                        {t("L_CHCE_FILE")}
                    </label>

                    <div className="flex flex-1 px-1.5 items-center gap-1 max-w-[90%] overflow-hidden whitespace-nowrap text-ellipsis">
                        <span
                            className={classNames(!!fileNm && "cursor-pointer underline text-blue")}
                            onClick={() => {
                                if (!fileNm) return;
                                downloadFile(data);
                            }}
                            title={fileNm || name || "No file."}
                        >
                            {fileNm || name || "No file."}
                        </span>
                        {pdfViewer && <IconButton icon="pdf" size="sm" onClick={() => openPdf(data)} />}
                    </div>

                    {!!data && (
                        <div className="flex items-center  max-w-[20%] overflow-hidden whitespace-nowrap text-ellipsis">
                            {data.status === "pending" && (
                                <IconButton icon="loading" className="animate-spin text-blue" size="xs" />
                            )}
                            {(data.status === "error" || data.status === "success") && (
                                <IconButton
                                    icon="exclamationTriangle"
                                    className="text-error"
                                    size="xs"
                                    onClick={() => retry(data)}
                                />
                            )}
                            {_edit && !disableRemoveButton && (
                                <IconButton
                                    icon="minus"
                                    className="text-error"
                                    size="xs"
                                    onClick={() => remove(data)}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Multi = (props: any) => {
    const { t } = useTranslation();

    const {
        _edit,
        data,
        accept,
        handleChange,
        removeChecked,
        disableHeader,
        disableName,
        disableTitle,
        disableDescription,
        disableRemoveButton,
        checked,
        retry,
        disableDelete,
        downloadFile,
        handleCheck,
        handleChangeName,
        handleChangeTitle,
        handleChangeDescription,
        readOnlyName,
        readOnlyTitle,
        readOnlyDescription,
        valueType,
    } = props;

    return (
        <div className="w-full flex flex-col gap-1">
            {_edit && (
                <div className="flex gap-1 justify-end">
                    <input hidden accept={accept} type="file" multiple={true} onChange={handleChange} />
                    <Button
                        role="add"
                        onClick={(event) => {
                            //@ts-ignore
                            event.currentTarget.previousSibling?.click?.();
                        }}
                    />
                    {!disableRemoveButton && <Button role="delete" onClick={removeChecked} />}
                </div>
            )}
            <table className="[&_td]:border [&_td]:p-1">
                {!disableHeader && (
                    <thead className="bg-header h-8">
                        <tr>
                            {_edit && <td className="w-10">{/* <input type="checkbox" /> */}</td>}
                            {valueType !== "file" && !disableName && <td>Name</td>}
                            {valueType !== "file" && !disableTitle && <td>Title</td>}
                            {valueType !== "file" && !disableDescription && <td>Description</td>}
                            <td>File</td>
                        </tr>
                    </thead>
                )}
                <tbody>
                    {!data.length && (
                        <tr>
                            <td colSpan={5}>No file.</td>
                        </tr>
                    )}
                    {data.map((item: any) => {
                        const key = item.key || item.fileUuid;
                        return (
                            <TableItem
                                key={key}
                                rootEdit={_edit}
                                checked={checked}
                                data={item}
                                retry={retry}
                                download={downloadFile}
                                handleCheck={handleCheck}
                                handleChangeName={handleChangeName}
                                handleChangeTitle={handleChangeTitle}
                                handleChangeDescription={handleChangeDescription}
                                valueType={valueType}
                                // options
                                readOnlyName={readOnlyName}
                                readOnlyTitle={readOnlyTitle}
                                readOnlyDescription={readOnlyDescription}
                                disableName={disableName}
                                disableTitle={disableTitle}
                                disableDescription={disableDescription}
                                disableDelete={disableDelete}
                            />
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
